import { isAndroid, isWeb } from 'src/shared';
import {
  GeneralCallbackResult,
  GeneralCallback,
  GeneralInvokeOption,
  IAnyStrObject,
  InvokeFinishedCallback
} from 'src/types';

export type IAnyNumObject = Record<number, any>;

export interface InvokeOption extends GeneralInvokeOption {}

export interface InvokeInnerFnsOption {
  beforeAll: GeneralCallback;
  beforeSuccess: GeneralCallback;
  afterSuccess: GeneralCallback;
  beforeFail: GeneralCallback;
  afterFail: GeneralCallback;
  afterAll: GeneralCallback;
}

// invoke callbacks
const callbacks: IAnyNumObject = {};
let callbackIndex: number = 0;

const defaultEventHandlers: any = {};

function invokeHandler(
  command: string,
  inputParams: string,
  callbackId: number
) {
  if (isWeb) {
    window.simulatorWebkit.messageHandlers.invoke.postMessage({
      command,
      inputParams,
      callbackId,
      type: 'legacy'
    });
  } else if (isAndroid) {
    window.viewLayerNative.invoke(command, inputParams, callbackId);
  } else {
    window.webkit.messageHandlers.invoke.postMessage({
      command,
      inputParams,
      callbackId
    });
  }
}

export function invoke(
  command: string,
  inputParams: IAnyStrObject = {},
  callback: InvokeFinishedCallback
) {
  const paramsString = JSON.stringify(inputParams);
  const callbackId = ++callbackIndex;
  callbacks[callbackId] = callback;
  invokeHandler(command, paramsString, callbackId);
}

/**
 * 增加promise处理
 */
export function beforeInvoke(
  apiName: string,
  opts: Partial<InvokeOption & IAnyStrObject> = {},
  innerFns: Partial<InvokeInnerFnsOption> = {}
) {
  if (!opts.success && !opts.fail && !opts.complete) {
    return new Promise((resolve, reject) => {
      opts.success = resolve;
      opts.fail = reject;
      invokeMethod(apiName, opts, innerFns);
    });
  }
  return invokeMethod(apiName, opts, innerFns);
}

/**
 * 同步调用 Native方法, 利用 window.prompt hook
 * @param {*} command 同步调用方法名
 * @param {*} params 方法参数
 */
export function invokeSync(command: string, params?: Partial<IAnyStrObject>) {
  // 同步API统一包一层 data, 方便后续 parse
  const inputParams = JSON.stringify({ command, ...params });
  // eslint-disable-next-line no-alert
  return window.prompt(inputParams);
}

/**
 * 统一封装调用逻辑, 通知 Native, 调用 API
 */
export function invokeMethod(
  apiName: string,
  opts?: Partial<InvokeOption & IAnyStrObject>,
  innerCb: Partial<InvokeInnerFnsOption> = {}
) {
  const invokeCbFnsObj: Record<string, GeneralCallback> = {};

  for (const name in opts) {
    if (typeof opts[name] === 'function') {
      invokeCbFnsObj[name] = opts[name];
      delete opts[name];
    }
  }

  invoke(apiName, opts, (invokeStatusCode, res) => {
    const isOk = Number(invokeStatusCode) === 0;
    const isFail = [1, -1].includes(Number(invokeStatusCode));
    if (isOk) {
      innerCb?.beforeAll?.(res);
      innerCb?.beforeSuccess?.(res);
      invokeCbFnsObj.success?.(res);
      innerCb?.afterSuccess?.(res);
    } else if (isFail) {
      innerCb?.beforeFail?.(res);
      invokeCbFnsObj.fail?.(res);
      innerCb?.afterFail?.(res);
    } else {
      innerCb?.beforeSuccess?.(res);
      invokeCbFnsObj?.preload?.(res);
    }
    if (isOk || isFail) {
      invokeCbFnsObj.complete?.(res);
      innerCb?.afterAll?.(res);
    }
  });
}

/**
 * Native notify Web Page invoke result
 *
 * => viewLayer.onInvokeFinished
 */
export function onInvokeFinished(
  callbackId: number,
  invokeStatusCode: number,
  outputParams: GeneralCallbackResult
) {
  const callback = callbacks[callbackId];
  if (typeof callback === 'function') {
    callback(invokeStatusCode, outputParams);
  }
  if (Number(invokeStatusCode) !== 2) {
    delete callbacks[callbackId];
  }
}

export function notifyNative(command: string, params: Object = {}) {
  const inputParams = JSON.stringify(params);
  if (isWeb) {
    window.simulatorWebkit.messageHandlers.notifyNative.postMessage({
      command,
      inputParams,
      type: 'legacy'
    });
  } else if (isAndroid) {
    window.viewLayerNative.notifyNative(command, inputParams);
  } else {
    window.webkit.messageHandlers.notifyNative.postMessage({
      command,
      inputParams
    });
  }
}

// 监听基础事件
export function onBasicEvent(event: string, handler: Function) {
  defaultEventHandlers[event] = handler;
}

// 取消监听基础事件
export function offBasicEvent(event: string) {
  defaultEventHandlers[event] = undefined;
}

// 执行注册的回调
export function handlerCall(handler: Function, webviewId: string, params: any) {
  if (typeof handler === 'function') {
    const webId = webviewId || params.webviewId || '';
    handler(params, webId);
  }
}

export function onNativeNotify(event: string, webviewId: string, params: any) {
  // 执行注册的回调
  const handler = defaultEventHandlers[event];
  handlerCall(handler, webviewId, params);
}

const MacleJSBridge = {
  invoke,
  invokeMethod,
  notifyNative,
  invokeSync,
  onNativeNotify,
  onBasicEvent,
  offBasicEvent
};

window.logicLayer = MacleJSBridge;

export default MacleJSBridge;
